import { useCallback, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import LogoIcon from 'components/App/Header/Logo/LogoIcon'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import LegalText from 'components/Account/AccountAccess/AccountAccessLegalText'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { agentHubOrSubBrandConfig } from 'agentHub/constants'
import { oidcSignInRedirect } from 'agentHub/auth/oidc/signIn'
import { reportClientError } from 'services/errorReportingService'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { useAppSelector } from 'hooks/reduxHooks'
import { selectLoggedIn } from 'selectors/accountSelectors'
import type { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal.types'

interface Props {
  dismissable?: boolean
}

// The default sign-in form for leagenthub (see useIsLoginV2Enabled). One button
// starts the OIDC authorize redirect.
function AgentHubLoginV2Form({ dismissable }: Props) {
  const intl = useIntl()
  const [isRedirecting, setIsRedirecting] = useState(false)
  const modalContext = useModalElementContext<AccountAccessModalResult>()
  const loggedIn = useAppSelector(selectLoggedIn)

  // Every other form in this family resolves on `loggedIn`; this one had no such
  // effect, so a session restore signing the user in behind the modal left the
  // prompt sitting over a logged-in page.
  useEffect(() => {
    if (loggedIn) {
      modalContext?.resolve({ loggedIn: true })
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn])

  const handleSignIn = useCallback(() => {
    setIsRedirecting(true)
    oidcSignInRedirect().catch((error) => {
      setIsRedirecting(false)
      reportClientError(error, { context: 'oidcSignInRedirect' }).catch(() => undefined)
      showSnackbar(
        intl.formatMessage({
          id: 'MCKts9GjL1ZL5f1_-z0e',
          defaultMessage: 'Could not start sign-in. Please try again.',
          description: 'Error snackbar shown when the agent hub OIDC sign-in redirect fails to start',
        }),
        'critical',
      )
    })
  }, [intl])

  return <>
    <ModalHeader title="" dismissible={dismissable} />
    <ModalBody>
      <ModalContent>
        <Group direction="vertical" gap={24} data-testid="agent-hub-login-v2-form">
          <Group direction="horizontal" horizontalAlign="center"><LogoIcon /></Group>

          <Group direction="vertical" gap={4}>
            <Heading variant="heading4" align="center">
              {intl.formatMessage({
                id: 'fqXEN6y4cfbCyzLCFwi0',
                defaultMessage: 'Welcome back!',
                description: 'Title on the agent hub Auth V2 (OIDC) login modal shown above the sign-in button',
              })}
            </Heading>
            <BodyText variant="medium" align="center">
              {intl.formatMessage({
                id: 'vpwWvhmMcLEoYRb6qSSa',
                defaultMessage: 'Sign in to access your {displayShortName} portal.',
                description: 'Subtitle on the agent hub Auth V2 (OIDC) login modal shown beneath the welcome title',
              }, {
                displayShortName: agentHubOrSubBrandConfig.displayShortName,
              })}
            </BodyText>
          </Group>

          <TextButton
            kind="primary"
            size="large"
            data-testid="agent-hub-login-v2-button"
            fit="flex"
            disabled={isRedirecting}
            onClick={handleSignIn}
          >
            {intl.formatMessage({
              id: '1hLDTxBcMHmdzmyfXnK3',
              defaultMessage: 'Sign in with email and password',
              description: 'Agent Hub OIDC login button that starts the sign-in redirect',
            })}
          </TextButton>

          <LegalText />
        </Group>
      </ModalContent>
    </ModalBody>
  </>
}

export default AgentHubLoginV2Form
